'use client';

import { Menu as BaseMenu } from '@base-ui-components/react/menu';
import { Check, ChevronRight, Circle } from 'lucide-react';

import { cn } from '../../utils/cn';
import { useInheritedColorScheme } from '../../utils/use-inherited-color-scheme';
import './dropdown-menu.css';

interface DropdownMenuProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

function DropdownMenu({ children, ...props }: DropdownMenuProps) {
  return <BaseMenu.Root {...props}>{children}</BaseMenu.Root>;
}

interface DropdownMenuTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Render a custom element as the trigger (Base UI `render` passthrough).
   * Lets a styled element (e.g. a DS `Button`) be the trigger without `asChild`.
   * Typed from Base UI's own trigger so the passthrough stays exact.
   */
  render?: React.ComponentProps<typeof BaseMenu.Trigger>['render'];
}

function DropdownMenuTrigger({
  className,
  ...props
}: DropdownMenuTriggerProps) {
  return (
    <BaseMenu.Trigger
      className={cn(className)}
      {...(props as React.ComponentProps<typeof BaseMenu.Trigger>)}
    />
  );
}

interface DropdownMenuContentProps extends React.HTMLAttributes<HTMLDivElement> {
  side?: 'top' | 'right' | 'bottom' | 'left';
  sideOffset?: number;
  align?: 'start' | 'center' | 'end';
}

function DropdownMenuContent({
  className,
  side = 'bottom',
  sideOffset = 4,
  align = 'center',
  children,
  ...props
}: DropdownMenuContentProps) {
  const { probeRef, themeAttrs } = useInheritedColorScheme();

  return (
    <>
      <span ref={probeRef} style={{ display: 'none' }} />
      <BaseMenu.Portal>
        <BaseMenu.Positioner side={side} sideOffset={sideOffset} align={align}>
          <BaseMenu.Popup
            {...themeAttrs}
            className={cn(
              'dropdown-menu-content bg-popover text-popover-foreground z-50 min-w-[8rem] overflow-hidden rounded-md border p-1 shadow-md',
              'transition-all duration-150',
              'data-[starting-style]:scale-95 data-[starting-style]:opacity-0',
              'data-[ending-style]:scale-95 data-[ending-style]:opacity-0',
              className
            )}
            {...(props as React.ComponentProps<typeof BaseMenu.Popup>)}
          >
            {children}
          </BaseMenu.Popup>
        </BaseMenu.Positioner>
      </BaseMenu.Portal>
    </>
  );
}

interface DropdownMenuItemProps extends React.HTMLAttributes<HTMLDivElement> {
  inset?: boolean;
  disabled?: boolean;
}

function DropdownMenuItem({
  className,
  inset,
  ...props
}: DropdownMenuItemProps) {
  return (
    <BaseMenu.Item
      className={cn(
        'relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none select-none',
        'data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        inset && 'pl-8',
        className
      )}
      {...(props as React.ComponentProps<typeof BaseMenu.Item>)}
    />
  );
}

interface DropdownMenuCheckboxItemProps extends React.HTMLAttributes<HTMLDivElement> {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
}

function DropdownMenuCheckboxItem({
  className,
  children,
  checked,
  onCheckedChange,
  ...props
}: DropdownMenuCheckboxItemProps) {
  const itemProps = {
    checked,
    onCheckedChange: onCheckedChange
      ? (val: boolean) => onCheckedChange(val)
      : undefined,
    ...props,
  } as React.ComponentProps<typeof BaseMenu.CheckboxItem>;
  return (
    <BaseMenu.CheckboxItem
      className={cn(
        'relative flex cursor-default items-center rounded-sm py-1.5 pr-2 pl-8 text-sm outline-none select-none',
        'data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className
      )}
      {...itemProps}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <BaseMenu.CheckboxItemIndicator>
          <Check className="h-4 w-4" />
        </BaseMenu.CheckboxItemIndicator>
      </span>
      {children}
    </BaseMenu.CheckboxItem>
  );
}

interface DropdownMenuRadioGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  value?: string;
  onValueChange?: (value: string) => void;
}

function DropdownMenuRadioGroup({
  className,
  value,
  onValueChange,
  children,
  ...props
}: DropdownMenuRadioGroupProps) {
  const groupProps = {
    value,
    onValueChange: onValueChange
      ? (val: string) => onValueChange(val)
      : undefined,
    ...props,
  } as React.ComponentProps<typeof BaseMenu.RadioGroup>;
  return (
    <BaseMenu.RadioGroup className={cn(className)} {...groupProps}>
      {children}
    </BaseMenu.RadioGroup>
  );
}

interface DropdownMenuRadioItemProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
  disabled?: boolean;
}

function DropdownMenuRadioItem({
  className,
  children,
  ...props
}: DropdownMenuRadioItemProps) {
  return (
    <BaseMenu.RadioItem
      className={cn(
        'relative flex cursor-default items-center rounded-sm py-1.5 pr-2 pl-8 text-sm outline-none select-none',
        'data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className
      )}
      {...(props as React.ComponentProps<typeof BaseMenu.RadioItem>)}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <BaseMenu.RadioItemIndicator>
          <Circle className="h-2 w-2 fill-current" />
        </BaseMenu.RadioItemIndicator>
      </span>
      {children}
    </BaseMenu.RadioItem>
  );
}

function DropdownMenuLabel({
  className,
  inset,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { inset?: boolean }) {
  return (
    <BaseMenu.GroupLabel
      className={cn(
        'px-2 py-1.5 text-sm font-semibold',
        inset && 'pl-8',
        className
      )}
      {...(props as React.ComponentProps<typeof BaseMenu.GroupLabel>)}
    />
  );
}

function DropdownMenuSeparator({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <BaseMenu.Separator
      className={cn('bg-muted -mx-1 my-1 h-px', className)}
      {...(props as React.ComponentProps<typeof BaseMenu.Separator>)}
    />
  );
}

function DropdownMenuShortcut({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className={cn(
        'text-muted-foreground ml-auto text-xs tracking-widest',
        className
      )}
      {...props}
    />
  );
}

function DropdownMenuGroup({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <BaseMenu.Group
      className={cn(className)}
      {...(props as React.ComponentProps<typeof BaseMenu.Group>)}
    />
  );
}

interface DropdownMenuSubProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

function DropdownMenuSub({ children, ...props }: DropdownMenuSubProps) {
  return <BaseMenu.Root {...props}>{children}</BaseMenu.Root>;
}

interface DropdownMenuSubTriggerProps extends React.HTMLAttributes<HTMLDivElement> {
  inset?: boolean;
  disabled?: boolean;
}

function DropdownMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}: DropdownMenuSubTriggerProps) {
  return (
    <BaseMenu.SubmenuTrigger
      className={cn(
        'flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none select-none',
        'data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground',
        inset && 'pl-8',
        className
      )}
      {...(props as React.ComponentProps<typeof BaseMenu.SubmenuTrigger>)}
    >
      {children}
      <ChevronRight className="ml-auto h-4 w-4" />
    </BaseMenu.SubmenuTrigger>
  );
}

interface DropdownMenuSubContentProps extends React.HTMLAttributes<HTMLDivElement> {
  side?: 'top' | 'right' | 'bottom' | 'left';
  sideOffset?: number;
}

function DropdownMenuSubContent({
  className,
  side = 'right',
  sideOffset = -4,
  children,
  ...props
}: DropdownMenuSubContentProps) {
  const { probeRef, themeAttrs } = useInheritedColorScheme();

  return (
    <>
      <span ref={probeRef} style={{ display: 'none' }} />
      <BaseMenu.Portal>
        <BaseMenu.Positioner side={side} sideOffset={sideOffset}>
          <BaseMenu.Popup
            {...themeAttrs}
            className={cn(
              'dropdown-menu-content bg-popover text-popover-foreground z-50 min-w-[8rem] overflow-hidden rounded-md border p-1 shadow-lg',
              'transition-all duration-150',
              'data-[starting-style]:scale-95 data-[starting-style]:opacity-0',
              'data-[ending-style]:scale-95 data-[ending-style]:opacity-0',
              className
            )}
            {...(props as React.ComponentProps<typeof BaseMenu.Popup>)}
          >
            {children}
          </BaseMenu.Popup>
        </BaseMenu.Positioner>
      </BaseMenu.Portal>
    </>
  );
}

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
};
export type {
  DropdownMenuProps,
  DropdownMenuTriggerProps,
  DropdownMenuContentProps,
  DropdownMenuItemProps,
  DropdownMenuCheckboxItemProps,
  DropdownMenuRadioGroupProps,
  DropdownMenuRadioItemProps,
  DropdownMenuSubProps,
  DropdownMenuSubTriggerProps,
  DropdownMenuSubContentProps,
};
